import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import { isDatadogSyntheticsBrowser } from '@/config';
import { env } from '@/env';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { loadScript } from '@/utils/dom';
import { logger } from '@/utils/logger';

declare global {
  interface Window {
    Cypress?: unknown;
    _sva?: {
      traits?: Record<string, unknown>;
      setVisitorTraits?: (traits: Record<string, unknown>) => void;
    };
  }
}

type SurvicateTraits = Record<string, string | number | undefined>;

export const useSurvicateStore = defineStore('survicate', () => {
  const flagStore = useFlagStore();
  const identityStore = useIdentityStore();
  const ready = ref(false);
  const initCalled = ref(false);
  const initializing = ref(false);

  const workspaceKey = env.survicateWorkspaceKey;
  const enabled = env.survicateEnabled && !isDatadogSyntheticsBrowser;

  function buildTraits(): SurvicateTraits | null {
    const identity = identityStore.identity;
    const currentBrand = identityStore.currentBrand;
    if (!identity) return null;

    let daysAsCustomer = -1;
    if (identity?.created_at) {
      const userCreatedAt = dayjs(identity.created_at);
      daysAsCustomer = dayjs().diff(userCreatedAt, 'd');
    } else {
      logger.warn('[Survicate] identity.created_at is missing — daysAsCustomer will be -1');
    }

    const plan = currentBrand?.plan;
    let currentBrandDaysAsCustomer = -1;
    if (plan?.created_at) {
      const planCreatedAt = dayjs(plan.created_at);
      currentBrandDaysAsCustomer = dayjs().diff(planCreatedAt, 'd');
    }

    return {
      email: identity.email,
      firstName: identity.first_name,
      lastName: identity.last_name,
      jobTitle: identity.job_title,
      daysAsCustomer,
      organizationName: identity.organization?.name,
      organizationCustomerStage: identity.organization?.customer_stage,
      currentBrandName: currentBrand?.name,
      currentBrandDaysAsCustomer,
      currentBrandPlanType: currentBrand?.plan?.plan_type,
      currentBrandPlanCustomerStage: plan?.customer_stage,
    };
  }

  function setTraits(traits: SurvicateTraits): void {
    window._sva = window._sva ?? {};
    window._sva.traits = traits;
    if (typeof window._sva?.setVisitorTraits === 'function') {
      window._sva.setVisitorTraits(traits);
    }
  }

  function init(): void {
    initCalled.value = true;
    if (
      !window.Cypress &&
      enabled &&
      flagStore.flags.surveySurvicate &&
      !ready.value &&
      !initializing.value
    ) {
      initializing.value = true;

      // Set traits before loading the script so the SDK can read them on init
      const traits = buildTraits();
      if (traits) {
        setTraits(traits);
      }

      const scriptUrl = `https://survey.survicate.com/workspaces/${workspaceKey}/web_surveys.js`;
      loadScript(scriptUrl, {
        id: 'survicate-widget',
        onload: () => {
          ready.value = true;
          logger.info('[Survicate] Survey script loaded', { workspaceKey });
        },
        onerror: () => {
          initializing.value = false;
          logger.error('[Survicate] Failed to load survey script', { workspaceKey, scriptUrl });
        },
      });
    }
  }

  watch(
    () => flagStore.flags.surveySurvicate,
    (flagValue) => {
      if (flagValue && initCalled.value && !initializing.value) {
        init();
      }
    },
  );

  // Update traits when identity or brand changes after script is loaded
  watch(
    () => ({
      identity: identityStore.identity,
      currentBrand: identityStore.currentBrand,
      ready: ready.value,
    }),
    (value) => {
      if (value.ready && value.identity) {
        const traits = buildTraits();
        if (traits) {
          setTraits(traits);
        }
      }
    },
    {
      immediate: true,
    },
  );

  return {
    init,
    ready,
  };
});
